import { useEffect, useState, useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { UseQueryResult } from '@tanstack/react-query';

import { useDebouncedValue } from '@/react/hooks/useDebouncedValue';
import { EnvironmentId } from '@/react/portainer/environments/types';
import PortainerError from '@/portainer/error';

import { ExpandableMessageByLines } from '@@/ExpandableMessageByLines';
import { FormSection } from '@@/form-components/FormSection';
import { CodeEditor } from '@@/CodeEditor';
import { DiffViewer } from '@@/CodeEditor/DiffViewer';
import { InlineLoader } from '@@/InlineLoader';
import { Alert } from '@@/Alert';
import { TextTip } from '@@/Tip/TextTip';
import { Badge } from '@@/Badge';
import { Icon } from '@@/Icon';

import { useHelmDryRun } from '../helmReleaseQueries/useHelmDryRun';
import { useGitHelmDryRun } from '../helmReleaseQueries/useGitHelmDryRun';
import { GitHelmDryRunPayload } from '../helmReleaseQueries/gitHelmTypes';
import { HelmRelease } from '../types';

type ManualHelmProps = {
  mode: 'manual'; // eslint-disable-line react/no-unused-prop-types
  name: string;
  namespace: string;
  chart: string;
  version: string;
  repo?: string;
  registryId?: number;
  values: string;
  environmentId: EnvironmentId;
};

type GitHelmProps = {
  mode: 'git'; // eslint-disable-line react/no-unused-prop-types
  payload: GitHelmDryRunPayload;
  environmentId: EnvironmentId;
};

type BaseProps = {
  onChangePreviewValidation: (isValid: boolean) => void;
  currentManifest?: string; // For upgrade diff view
  title: string;
  className?: string;
};

type Props = BaseProps & (ManualHelmProps | GitHelmProps);

export function ManifestPreviewFormSection(props: Props) {
  const {
    mode,
    environmentId,
    onChangePreviewValidation,
    currentManifest,
    title,
    className,
  } = props;

  if (mode === 'git') {
    const { payload } = props;
    return (
      <GitManifestPreview
        className={className}
        payload={payload}
        environmentId={environmentId}
        onChangePreviewValidation={onChangePreviewValidation}
        currentManifest={currentManifest}
        title={title}
        mode="git"
      />
    );
  }

  const { name, namespace, chart, version, repo, registryId, values } = props;
  return (
    <ManualManifestPreview
      className={className}
      mode="manual"
      name={name}
      namespace={namespace}
      chart={chart}
      version={version}
      repo={repo}
      registryId={registryId}
      values={values}
      environmentId={environmentId}
      onChangePreviewValidation={onChangePreviewValidation}
      currentManifest={currentManifest}
      title={title}
    />
  );
}

// Separate component for Git mode to avoid conditional hook calls
function GitManifestPreview({
  payload,
  environmentId,
  onChangePreviewValidation,
  currentManifest,
  title,
  className,
}: GitHelmProps & BaseProps) {
  const debouncedPayload = useDebouncedValue(payload, 500);
  const manifestPreviewQuery = useGitHelmDryRun(
    environmentId,
    debouncedPayload
  );

  return (
    <ManifestPreviewContent
      manifestPreviewQuery={manifestPreviewQuery}
      onChangePreviewValidation={onChangePreviewValidation}
      currentManifest={currentManifest}
      title={title}
      className={className}
      hasRequiredFields={
        !!(
          debouncedPayload.name &&
          debouncedPayload.namespace &&
          debouncedPayload.repositoryURL &&
          debouncedPayload.helmChartPath
        )
      }
    />
  );
}

// Separate component for Manual mode to avoid conditional hook calls
function ManualManifestPreview({
  name,
  namespace,
  chart,
  version,
  repo,
  registryId,
  values,
  environmentId,
  onChangePreviewValidation,
  currentManifest,
  title,
  className,
}: ManualHelmProps & BaseProps) {
  const payload = useMemo(
    () => ({
      name,
      namespace,
      chart,
      version,
      repo,
      registryId,
      values,
    }),
    [name, namespace, chart, version, repo, registryId, values]
  );

  const debouncedPayload = useDebouncedValue(payload, 500);
  const manifestPreviewQuery = useHelmDryRun(environmentId, debouncedPayload);

  return (
    <ManifestPreviewContent
      className={className}
      manifestPreviewQuery={manifestPreviewQuery}
      onChangePreviewValidation={onChangePreviewValidation}
      currentManifest={currentManifest}
      title={title}
      hasRequiredFields={
        !!(
          debouncedPayload.name &&
          debouncedPayload.namespace &&
          debouncedPayload.chart
        )
      }
    />
  );
}

// Shared display component
function ManifestPreviewContent({
  manifestPreviewQuery,
  onChangePreviewValidation,
  currentManifest,
  title,
  hasRequiredFields,
  className,
}: {
  manifestPreviewQuery: UseQueryResult<HelmRelease, PortainerError>;
  onChangePreviewValidation: (isValid: boolean) => void;
  currentManifest?: string;
  title: string;
  hasRequiredFields: boolean;
  className?: string;
}) {
  const [isFolded, setIsFolded] = useState(true);

  useEffect(() => {
    onChangePreviewValidation(!manifestPreviewQuery.isError);
  }, [manifestPreviewQuery.isError, onChangePreviewValidation]);

  if (!hasRequiredFields) {
    return null;
  }

  // Only show loading state or the error to keep the view simple
  if (manifestPreviewQuery.isInitialLoading) {
    return (
      <InlineLoader>
        Generating manifest preview (this may take a minute)...{' '}
      </InlineLoader>
    );
  }

  return (
    <FormSection
      title={
        <span>
          {title}
          {manifestPreviewQuery.isError && (
            <Badge
              type="dangerSecondary"
              className="ml-2"
              data-cy="helm-manifest-preview-error-badge"
            >
              <Icon icon={AlertTriangle} size="md" />
            </Badge>
          )}
        </span>
      }
      isFoldable
      defaultFolded={isFolded}
      setIsDefaultFolded={setIsFolded}
      id="manifest-preview"
      className={className}
    >
      {manifestPreviewQuery.isError ? (
        <Alert color="error" title="Error with Helm chart configuration">
          <ExpandableMessageByLines>
            {manifestPreviewQuery.error?.err instanceof Error
              ? manifestPreviewQuery.error.err.message
              : manifestPreviewQuery.error?.message ||
                'Error generating manifest preview'}
          </ExpandableMessageByLines>
        </Alert>
      ) : (
        <ManifestPreview
          currentManifest={currentManifest}
          newManifest={manifestPreviewQuery.data?.manifest ?? ''}
        />
      )}
    </FormSection>
  );
}

function ManifestPreview({
  currentManifest,
  newManifest,
}: {
  currentManifest?: string;
  newManifest: string;
}) {
  if (!newManifest) {
    return <TextTip color="blue">No manifest preview available</TextTip>;
  }

  if (currentManifest) {
    return (
      <DiffViewer
        originalCode={currentManifest}
        newCode={newManifest}
        id="manifest-preview"
        data-cy="manifest-diff-preview"
        type="yaml"
      />
    );
  }

  return (
    <CodeEditor
      id="manifest-preview"
      value={newManifest}
      data-cy="manifest-preview"
      type="yaml"
      readonly
      showToolbar={false}
    />
  );
}
